import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Collapse,
  Progress,
  Alert,
  Tabs,
  Tag,
  Space,
  Tooltip
} from 'antd';
import {
  DollarOutlined,
  TrophyOutlined,
  BankOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const { Panel } = Collapse;
const { TabPane } = Tabs;

const ResultsDisplay = ({ simulationResult }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!simulationResult) {
    return (
      <Card>
        <Alert
          message="No Results"
          description="Run a simulation to see results"
          type="info"
          showIcon
        />
      </Card>
    );
  }

  const formatCurrency = (value) => {
    return value ? value.toLocaleString('ru-RU', { minimumFractionDigits: 2 }) : '0.00';
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  // Data for charts
  const dailyData = simulationResult.daily_summaries.map(day => ({
    day: day.day,
    safe: day.pool_safe_balance,
    medium: day.pool_medium_balance,
    risky: day.pool_risky_balance,
    treasury: day.treasury_balance,
    jackpots: day.jackpots_balance,
    referrals: day.referrals_balance,
    winRate: day.player_win_percentage
  }));

  const legsData = simulationResult.legs_statistics.map(stat => ({
    legs: stat.legs,
    totalBets: stat.total_bets,
    wins: stat.wins,
    winRate: stat.win_percentage,
    expectedWinRate: stat.expected_win_percentage,
    houseEdge: stat.expected_house_edge,
    netResult: stat.net_result
  }));

  const poolData = [
    { name: 'Safe', value: simulationResult.final_pool_safe_balance, color: '#52c41a' },
    { name: 'Medium', value: simulationResult.final_pool_medium_balance, color: '#1890ff' },
    { name: 'Risky', value: simulationResult.final_pool_risky_balance, color: '#f5222d' }
  ];

  const aprData = Object.entries(simulationResult.pool_aprs).map(([pool, apr]) => ({
    pool,
    apr
  }));

  // Columns for leg statistics table
  const legsColumns = [
    {
      title: 'Legs',
      dataIndex: 'legs',
      key: 'legs',
      render: (value) => <Tag color="blue">{value}</Tag>
    },
    {
      title: 'Total Bets',
      dataIndex: 'total_bets',
      key: 'total_bets',
      render: (value) => value.toLocaleString('ru-RU')
    },
    {
      title: 'Wins',
      dataIndex: 'wins',
      key: 'wins',
      render: (value) => value.toLocaleString('ru-RU')
    },
    {
      title: 'Win Rate',
      dataIndex: 'win_percentage',
      key: 'win_percentage',
      render: (value) => (
        <span style={{ color: value > 50 ? '#52c41a' : '#f5222d' }}>
          {formatPercentage(value)}
        </span>
      )
    },
    {
      title: 'Expected %',
      dataIndex: 'expected_win_percentage',
      key: 'expected_win_percentage',
      render: (value) => formatPercentage(value)
    },
    {
      title: 'House Edge',
      dataIndex: 'expected_house_edge',
      key: 'expected_house_edge',
      render: (value) => (
        <span style={{ color: '#fa8c16' }}>
          {formatPercentage(value)}
        </span>
      )
    },
    {
      title: 'Total Stakes',
      dataIndex: 'total_stakes',
      key: 'total_stakes',
      render: (value) => `${formatCurrency(value)} Lumia`
    },
    {
      title: 'Total Payouts',
      dataIndex: 'total_payouts',
      key: 'total_payouts',
      render: (value) => `${formatCurrency(value)} Lumia`
    },
    {
      title: 'Net Result',
      dataIndex: 'net_result',
      key: 'net_result',
      render: (value) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#f5222d' }}>
          {value >= 0 ? '+' : ''}{formatCurrency(value)} Lumia
        </span>
      )
    }
  ];

  return (
    <Card title="Simulation Results" style={{ marginTop: 16 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Overview */}
        <TabPane tab={<span><BarChartOutlined />Overview</span>} key="overview">
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="Total Bets"
                value={simulationResult.total_bets_placed}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Stakes"
                value={formatCurrency(simulationResult.total_stakes_sum)}
                suffix="Lumia"
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Payouts"
                value={formatCurrency(simulationResult.total_player_payouts_sum)}
                suffix="Lumia"
                prefix={<BankOutlined />}
                valueStyle={{ color: '#f5222d' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Win Percentage"
                value={formatPercentage(simulationResult.overall_player_win_percentage)}
                prefix={<TrophyOutlined />}
                valueStyle={{ 
                  color: simulationResult.overall_player_win_percentage > 50 ? '#52c41a' : '#f5222d' 
                }}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Card size="small" title="Final Pool Balances">
                <Statistic
                  title="Safe Pool"
                  value={formatCurrency(simulationResult.final_pool_safe_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#52c41a' }}
                />
                <Statistic
                  title="Medium Pool"
                  value={formatCurrency(simulationResult.final_pool_medium_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Statistic
                  title="Risky Pool"
                  value={formatCurrency(simulationResult.final_pool_risky_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="Other Funds">
                <Statistic
                  title="Treasury"
                  value={formatCurrency(simulationResult.final_treasury_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#722ed1' }}
                />
                <Statistic
                  title="Jackpots"
                  value={formatCurrency(simulationResult.final_jackpots_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#fa8c16' }}
                />
                <Statistic
                  title="Referrals"
                  value={formatCurrency(simulationResult.final_referrals_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="Pool APRs">
                {Object.entries(simulationResult.pool_aprs).map(([pool, apr]) => (
                  <Statistic
                    key={pool}
                    title={`${pool} Pool`}
                    value={formatPercentage(apr)}
                    valueStyle={{ 
                      color: apr > 0 ? '#52c41a' : '#f5222d' 
                    }}
                  />
                ))}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Charts */}
        <TabPane tab={<span><LineChartOutlined />Charts</span>} key="charts">
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Card title="Pool Balance Changes by Day" size="small">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="safe" stroke="#52c41a" name="Safe" />
                    <Line type="monotone" dataKey="medium" stroke="#1890ff" name="Medium" />
                    <Line type="monotone" dataKey="risky" stroke="#f5222d" name="Risky" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Card title="Pool Distribution" size="small">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={poolData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {poolData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Pool APRs" size="small">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={aprData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="pool" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatPercentage(value)} />
                    <Bar dataKey="apr" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card title="Player Win Rate by Day" size="small">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatPercentage(value)} />
                    <Line type="monotone" dataKey="winRate" stroke="#fa8c16" name="Win Rate" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Leg Statistics */}
        <TabPane tab={<span><PieChartOutlined />Leg Statistics</span>} key="legs">
          <Table
            columns={legsColumns}
            dataSource={simulationResult.legs_statistics}
            rowKey="legs"
            pagination={false}
            size="small"
          />
        </TabPane>

        {/* Pool Protection */}
        <TabPane tab={<span><BankOutlined />Pool Protection</span>} key="protection">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Rejected Bets"
                value={simulationResult.pool_protection.bets_rejected_due_to_low_balance}
                valueStyle={{ color: '#f5222d' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Treasury Refills"
                value={simulationResult.pool_protection.pool_refills_from_treasury}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Refilled Amount"
                value={formatCurrency(simulationResult.pool_protection.total_refilled_amount)}
                suffix="Lumia"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Card size="small" title="Minimum Pool Balances">
                <Statistic
                  title="Safe Pool"
                  value={formatCurrency(simulationResult.min_pool_safe_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#52c41a' }}
                />
                <Statistic
                  title="Medium Pool"
                  value={formatCurrency(simulationResult.min_pool_medium_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Statistic
                  title="Risky Pool"
                  value={formatCurrency(simulationResult.min_pool_risky_balance)}
                  suffix="Lumia"
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="Money Flow Analysis">
                <Statistic
                  title="Total HE Generated"
                  value={formatCurrency(simulationResult.money_flow_analysis.total_he_generated)}
                  suffix="Lumia"
                  valueStyle={{ color: '#fa8c16' }}
                />
                <Statistic
                  title="HE to Treasury"
                  value={formatCurrency(simulationResult.money_flow_analysis.total_he_to_treasury)}
                  suffix="Lumia"
                  valueStyle={{ color: '#722ed1' }}
                />
                <Statistic
                  title="HE to Jackpots"
                  value={formatCurrency(simulationResult.money_flow_analysis.total_he_to_jackpots)}
                  suffix="Lumia"
                  valueStyle={{ color: '#fa8c16' }}
                />
                <Statistic
                  title="HE to Referrals"
                  value={formatCurrency(simulationResult.money_flow_analysis.total_he_to_referrals)}
                  suffix="Lumia"
                  valueStyle={{ color: '#13c2c2' }}
                />
                <Statistic
                  title="HE in Pools"
                  value={formatCurrency(simulationResult.money_flow_analysis.total_he_kept_in_pools)}
                  suffix="Lumia"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default ResultsDisplay;
